import { Component, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { DataService } from 'src/app/data.service';

/**
 *  PÉNZÜGY → TANDÍJ
 *
 *  Egyenleg = az éves kiírás − a családhoz rendelt bankpartnerek jóváírásai a fenntartó
 *  tandíj-számláján (a NAPLO bankszinkronjából). A család a sajátját látja; a kezelő
 *  (könyvelő / „Tandíj” admin) három nézetben dolgozik: egyenlegek, bankpartner-összerendelés,
 *  éves kiírás. A fül csak akkor látszik, ha a NAPLO-ban az összekötés be van állítva
 *  (lásd backend/tandij.js).
 *
 *  ZSEBEK: a támogatási díj mellett további gyűjtők (pl. „Zenit Start”). Mindegyikre külön
 *  lehet kiírni, a táblázatokban mindegyik saját oszlopot kap; a banki befizetés a közleménye
 *  kulcsszavai szerint kerül zsebbe, ez tételenként átírható.
 */
@Component({
  selector: 'app-tandij',
  templateUrl: './tandij.component.html',
  styleUrls: ['./tandij.component.scss'],
})
export class TandijComponent implements OnInit {
  intezmeny = '';
  loginmode: any = null;
  allapot: any = null;
  tanev = 0;
  tanevek: number[] = [];
  betolt = false;
  hiba = '';
  uzenet = '';

  // család
  sajat: any = null;

  // kezelő
  nezet: 'egyenlegek' | 'osszerendeles' | 'kiiras' | 'egyeb' = 'egyenlegek';
  egyeb: any = null;
  egyenlegek: any = null;
  kereses = '';
  csakHatralek = false;
  reszlet: any = null;
  partnerek: any = null;
  partnerKereses = '';
  csakRendeletlen = true;
  valasztas: Record<string, string> = {};
  kiirasMod: 'gyermek' | 'csalad' = 'gyermek';
  kiirasAlap: number | null = null;
  kiirasSorok: any[] = [];
  kiirasMent = false;
  /** A kiírás „Mindenkire alkalmaz” célja: '' = támogatási díj, különben a zseb azonosítója. */
  kiirasCel = '';
  zsebSzerk: { id: string; nev: string; kulcsszavak: string }[] = [];
  ujZseb = { nev: '', kulcsszavak: '' };
  zsebFut = false;

  /*  LEENDŐ CSALÁDOK (még be nem iratkozottak, pl. Zenit Start) – backend/tandij.js. */
  csakLeendo = false;
  leendoJavaslatok: any[] | null = null;
  leendoFut = false;
  leendoSzerk: { nev: string; gyerekek: string; szulok: string; megjegyzes: string } | null = null;
  osszevonCel = '';

  constructor(private http: HttpClient, private data: DataService) {}

  get csaladiNezet(): boolean {
    return this.loginmode === 'gondviselo' || this.loginmode === 'tanulo';
  }

  async ngOnInit() {
    const si: any = await this.data.getFromStorage('sajatIntezmeny');
    this.intezmeny = String(si?.intezmenyId || '').replace(/^\/+|\/+$/g, '');
    this.loginmode = await this.data.getFromStorage('loginmode');
    try {
      this.allapot = await firstValueFrom(this.http.get<any>(`/api/tandij/allapot/${this.intezmeny}`));
    } catch { this.allapot = { aktiv: false }; }
    this.tanev = Number(this.allapot?.tanev) || new Date().getFullYear();
    this.tanevek = [this.tanev - 1, this.tanev, this.tanev + 1];
    if (this.allapot?.aktiv) await this.frissit();
  }

  /** Forint magyar tagolással (szóköz az ezresek közt), tizedes nélkül. */
  ft(n: any): string {
    const v = Number(n);
    return Number.isFinite(v) ? Math.round(v).toLocaleString('hu-HU') : '–';
  }

  tanevNev(t: number) { return `${t}/${String((t + 1) % 100).padStart(2, '0')}`; }

  async frissit() {
    this.hiba = '';
    this.betolt = true;
    try {
      if (this.csaladiNezet) {
        this.sajat = await this.get(`/api/tandij/sajat/${this.intezmeny}?tanev=${this.tanev}`);
      } else if (this.nezet === 'egyenlegek') {
        this.egyenlegek = await this.get(`/api/tandij/egyenlegek/${this.intezmeny}?tanev=${this.tanev}`);
        if (this.reszlet) await this.reszletNyit(this.reszlet.csalad);
      } else if (this.nezet === 'osszerendeles') {
        this.partnerek = await this.get(`/api/tandij/partnerek/${this.intezmeny}?tanev=${this.tanev}`);
        this.valasztas = {};
        for (const p of this.partnerek?.partnerek || []) {
          if (p.javaslat) this.valasztas[p.kulcs] = p.javaslat;
          else if (p.javaslat_kategoria) this.valasztas[p.kulcs] = 'kat:' + p.javaslat_kategoria;
        }
      } else if (this.nezet === 'egyeb') {
        this.egyeb = await this.get(`/api/tandij/egyeb/${this.intezmeny}?tanev=${this.tanev}`);
      } else {
        this.egyenlegek = await this.get(`/api/tandij/egyenlegek/${this.intezmeny}?tanev=${this.tanev}`);
        this.kiirasElokeszit();
      }
    } catch (e: any) {
      this.hiba = e?.error?.error || 'Nem sikerült betölteni.';
    } finally { this.betolt = false; }
  }

  async nezetValt(n: any) { this.nezet = n; this.reszlet = null; await this.frissit(); }

  private get(url: string) { return firstValueFrom(this.http.get<any>(url)); }
  private post(url: string, body: any) { return firstValueFrom(this.http.post<any>(url, body)); }
  private jelez(s: string) { this.uzenet = s; setTimeout(() => { if (this.uzenet === s) this.uzenet = ''; }, 4000); }

  /* ── Egyenlegek ── */
  /** A további zsebek (a támogatási díjon kívül) – a betöltött nézetből. */
  get zsebek(): any[] { return this.egyenlegek?.zsebek || this.reszlet?.zseb_lista || []; }
  get foZseb(): string { return this.egyenlegek?.fo_zseb || this.reszlet?.fo_zseb || 'Támogatási díj'; }
  zsebNev(id: string): string {
    if (!id) return this.foZseb;
    return (this.zsebek.find((z: any) => z.id === id)?.nev) || '?';
  }

  get szurtCsaladok(): any[] {
    const k = this.kereses.trim().toLowerCase();
    return (this.egyenlegek?.csaladok || []).filter((c: any) =>
      (!this.csakHatralek || c.hatralekos || c.egyenleg > 0)
      && (!this.csakLeendo || c.leendo)
      && (!k || [c.nev, ...(c.szulok || []), ...(c.gyerekek || []).map((g: any) => g.osztaly)].join(' ').toLowerCase().includes(k)));
  }
  osztalyok(c: any) { if (c?.leendo) return 'leendő'; return [...new Set((c?.gyerekek || []).map((g: any) => g.osztaly).filter(Boolean))].join(', '); }
  szazalek(c: any) { return c?.kiirt > 0 ? Math.min(100, Math.round((c.befizetett / c.kiirt) * 100)) : 0; }

  async reszletNyit(csalad: string) {
    try {
      this.reszlet = await this.get(`/api/tandij/csalad/${csalad}/${this.intezmeny}?tanev=${this.tanev}`);
      this.leendoSzerk = null; this.osszevonCel = '';
    }
    catch (e: any) { this.hiba = e?.error?.error || 'Nem sikerült a család betöltése.'; }
  }

  /* ── Leendő családok ── */
  /** Beiratkozott (KRÉTÁ-s) családok – az összevonás célja. */
  get valodiCsaladok(): any[] { return (this.egyenlegek?.csaladok || []).filter((c: any) => !c.leendo); }
  get leendoCsaladokPartnerhez(): any[] { return (this.partnerek?.csaladok || []).filter((c: any) => c.leendo); }
  get beiratkozottCsaladokPartnerhez(): any[] { return (this.partnerek?.csaladok || []).filter((c: any) => !c.leendo); }

  async leendoJavaslatBetolt() {
    this.leendoFut = true;
    try {
      const r = await this.get(`/api/tandij/leendo-javaslatok/${this.intezmeny}?tanev=${this.tanev}`);
      this.leendoJavaslatok = (r?.javaslatok || []).map((j: any) => ({ ...j, kell: true, gyerekSzoveg: (j.gyerekek || []).join(', ') }));
    } catch (e: any) { this.hiba = e?.error?.error || 'Nem sikerült a javaslatok betöltése.'; }
    finally { this.leendoFut = false; }
  }
  get leendoKijeloltDb(): number { return (this.leendoJavaslatok || []).filter((j) => j.kell).length; }
  private gyerekLista(s: string): string[] { return String(s || '').split(/[,;]/).map((x) => x.trim()).filter(Boolean); }
  async leendoJavaslatFelvetel() {
    const lista = (this.leendoJavaslatok || []).filter((j) => j.kell);
    if (!lista.length) return;
    this.leendoFut = true;
    try {
      const r = await this.post(`/api/tandij/leendo/${this.intezmeny}`, { csaladok: lista.map((j) => ({
        nev: j.nev, gyerekek: this.gyerekLista(j.gyerekSzoveg), szulok: j.partner_nev,
        partner: { partner_szamla: j.partner_szamla, partner_nev: j.partner_nev } })) });
      this.jelez(`${r?.ids?.length || 0} leendő család felvéve, a befizetőjük hozzárendelve.`);
      this.leendoJavaslatok = null;
      await this.frissit();
    } catch (e: any) { this.hiba = e?.error?.error || 'Nem sikerült a felvétel.'; }
    finally { this.leendoFut = false; }
  }
  /** Egy befizetőből új leendő család (a Bankpartnerek listájából). */
  async leendoPartnerbol(p: any) {
    const nev = prompt('A leendő család neve (pl. a gyerek(ek) neve):', p.partner_nev || '');
    if (nev === null) return;
    const gyerekek = prompt('A gyerek(ek) neve, vesszővel elválasztva (üresen is hagyható):', nev) ?? '';
    try {
      await this.post(`/api/tandij/leendo/${this.intezmeny}`, { nev: nev.trim() || gyerekek, gyerekek: this.gyerekLista(gyerekek),
        szulok: p.partner_nev, partner: { partner_szamla: p.partner_szamla, partner_nev: p.partner_nev } });
      this.jelez(`${p.partner_nev} → új leendő család`);
      await this.frissit();
    } catch (e: any) { this.hiba = e?.error?.error || 'Nem sikerült a felvétel.'; }
  }
  leendoSzerkNyit() {
    const r = this.reszlet;
    this.leendoSzerk = { nev: r?.nev || '', gyerekek: (r?.gyerekek || []).map((g: any) => g.nev).join(', '),
      szulok: (r?.szulok || []).join('; '), megjegyzes: r?.megjegyzes || '' };
  }
  async leendoMent() {
    if (!this.reszlet || !this.leendoSzerk) return;
    try {
      await firstValueFrom(this.http.put(`/api/tandij/leendo/${this.reszlet.csalad}/${this.intezmeny}`, {
        nev: this.leendoSzerk.nev, gyerekek: this.gyerekLista(this.leendoSzerk.gyerekek),
        szulok: this.leendoSzerk.szulok, megjegyzes: this.leendoSzerk.megjegyzes }));
      this.leendoSzerk = null;
      this.jelez('A leendő család adatai mentve.');
      await this.frissit();
    } catch (e: any) { this.hiba = e?.error?.error || 'Nem sikerült menteni.'; }
  }
  async leendoTorol() {
    if (!this.reszlet || !confirm(`Törlöd a(z) „${this.reszlet.nev}” leendő családot?`)) return;
    try {
      await firstValueFrom(this.http.delete(`/api/tandij/leendo/${this.reszlet.csalad}/${this.intezmeny}`));
      this.reszlet = null;
      this.jelez('A leendő család törölve.');
      await this.frissit();
    } catch (e: any) { this.hiba = e?.error?.error || 'Nem sikerült törölni.'; }
  }
  async leendoOsszevon() {
    const cel = this.valodiCsaladok.find((c: any) => c.csalad === this.osszevonCel);
    if (!this.reszlet || !cel) return;
    if (!confirm(`A(z) „${this.reszlet.nev}” leendő család befizetői, befizetései és bizonylatai átkerülnek ide: „${cel.nev}”. Mehet?`)) return;
    try {
      await this.post(`/api/tandij/leendo/${this.reszlet.csalad}/osszevon/${this.intezmeny}`, { csalad: cel.csalad });
      this.jelez(`Összevonva: ${this.reszlet.nev} → ${cel.nev}`);
      await this.reszletNyit(cel.csalad);
      await this.frissit();
    } catch (e: any) { this.hiba = e?.error?.error || 'Nem sikerült az összevonás.'; }
  }
  async kizar(t: any) {
    const ok = t.kizarva ? '' : (prompt('Miért nem támogatás ez a jóváírás?', 'nem támogatás') ?? null);
    if (ok === null) return;
    try {
      await this.post(`/api/tandij/kizaras/${this.intezmeny}`, { tranzakcio_id: t.id, ok, vissza: !!t.kizarva });
      await this.frissit();
    } catch (e: any) { this.hiba = e?.error?.error || 'Nem sikerült.'; }
  }
  /*  KÉZI BEFIZETÉS (pl. készpénz). A család egyenlegébe ugyanúgy beszámít, mint a banki. */
  kezi: { datum: string; osszeg: number | null; mod: string; megjegyzes: string; zseb: string } =
    { datum: new Date().toLocaleDateString('sv-SE'), osszeg: null, mod: 'keszpenz', megjegyzes: '', zseb: '' };
  keziFut = false;

  async keziRogzit() {
    if (!this.reszlet?.csalad || !(Number(this.kezi.osszeg) > 0)) return;
    this.keziFut = true;
    try {
      await this.post(`/api/tandij/kezi/${this.intezmeny}`, { csalad: this.reszlet.csalad, ...this.kezi });
      this.jelez(`Rögzítve: ${this.ft(Number(this.kezi.osszeg))} Ft.`);
      this.kezi = { datum: new Date().toLocaleDateString('sv-SE'), osszeg: null, mod: 'keszpenz', megjegyzes: '', zseb: '' };
      await this.frissit();
    } catch (e: any) { this.hiba = e?.error?.error || 'Nem sikerült.'; }
    this.keziFut = false;
  }

  async keziTorol(b: any) {
    if (!confirm(`Törlöd a ${b.datum}-i, ${this.ft(b.osszeg)} Ft-os kézi befizetést?`)) return;
    try {
      await firstValueFrom(this.http.delete(`/api/tandij/kezi/${b.kezi_id}/${this.intezmeny}`));
      this.jelez('A kézi befizetés törölve.');
      await this.frissit();
    } catch (e: any) { this.hiba = e?.error?.error || 'Nem sikerült.'; }
  }

  /** A befizetés zsebe a választóban: 'auto' = a közlemény szerinti, 'k:<id>' = kézzel megadott. */
  zsebErtek(b: any): string { return b.zseb_kezi ? 'k:' + (b.zseb || '') : 'auto'; }
  async zsebBesorol(b: any, ertek: string) {
    const zseb = ertek === 'auto' ? null : ertek.slice(2);
    try {
      await this.post(`/api/tandij/zseb-besorolas/${this.intezmeny}`, { tetel_id: b.id, zseb });
      this.jelez(zseb === null ? 'A befizetés a közleménye szerinti zsebbe kerül.' : `A befizetés a(z) „${this.zsebNev(zseb)}” zsebbe került.`);
      await this.frissit();
    } catch (e: any) { this.hiba = e?.error?.error || 'Nem sikerült.'; }
  }

  /* ── Zsebek kezelése (a kiírás nézetben) ── */
  zsebSzerkElokeszit() {
    this.zsebSzerk = this.zsebek.map((z: any) => ({ id: z.id, nev: z.nev, kulcsszavak: z.kulcsszavak || '' }));
  }
  async zsebMent(z: { id?: string; nev: string; kulcsszavak: string }) {
    if (!z.nev?.trim()) { this.hiba = 'A zsebnek nevet kell adni.'; return; }
    this.zsebFut = true;
    try {
      await this.post(`/api/tandij/zseb/${this.intezmeny}`, { id: z.id || null, nev: z.nev.trim(), kulcsszavak: z.kulcsszavak || '' });
      this.jelez(z.id ? `„${z.nev}” mentve.` : `Új zseb: „${z.nev}”.`);
      if (!z.id) this.ujZseb = { nev: '', kulcsszavak: '' };
      await this.frissit();
    } catch (e: any) { this.hiba = e?.error?.error || 'Nem sikerült.'; }
    this.zsebFut = false;
  }
  async zsebTorol(z: any) {
    if (!confirm(`Törlöd a(z) „${z.nev}” zsebet? A kiírásai törlődnek, a befizetései visszakerülnek a(z) ${this.foZseb} zsebbe.`)) return;
    this.zsebFut = true;
    try {
      await firstValueFrom(this.http.delete(`/api/tandij/zseb/${z.id}/${this.intezmeny}`));
      this.jelez(`„${z.nev}” törölve.`);
      await this.frissit();
    } catch (e: any) { this.hiba = e?.error?.error || 'Nem sikerült.'; }
    this.zsebFut = false;
  }

  /** A befizetés számviteli bizonylata (a meglévő bizonylat-rendszer PDF-je, tokennel). */
  bizonylatNyit(b: any) {
    if (!b?.bizonylat_id) return;
    this.data.megnyitFajl(`/api/file/${this.intezmeny}/invoice/${b.bizonylat_id}.pdf`, `Bizonylat ${b.bizonylat_szam || ''}`.trim());
  }

  /** Befizetés átsorolása másik tanévhez (pl. előre fizetés). Üres válasz: vissza a dátum szerintibe. */
  async tanevAtsorol(b: any) {
    const most = b.atsorolva != null ? String(b.atsorolva) : String(this.tanev + 1);
    const v = prompt('Melyik tanévhez számítson ez a befizetés? A tanév kezdő éve (pl. ' + (this.tanev + 1)
      + ' = ' + this.tanevNev(this.tanev + 1) + '). Üresen hagyva a dátuma szerinti tanévbe kerül vissza.', most);
    if (v === null) return;
    const tanev = v.trim() === '' ? null : Number(v.trim());
    if (tanev !== null && !(tanev >= 2000 && tanev <= 2100)) { this.hiba = 'Hibás tanév.'; return; }
    try {
      await this.post(`/api/tandij/atsorolas/${this.intezmeny}`, { tetel_id: b.id, tanev });
      this.jelez(tanev === null ? 'A befizetés a dátuma szerinti tanévbe került.' : `A befizetés a ${this.tanevNev(tanev)} tanévhez számít.`);
      await this.frissit();
    } catch (e: any) { this.hiba = e?.error?.error || 'Nem sikerült.'; }
  }

  bizFut = false;
  /** A hiányzó bizonylatok elkészítése most (különben félóránként magától fut). */
  async bizonylatGeneral() {
    this.bizFut = true;
    try {
      const r: any = await this.post(`/api/tandij/bizonylatok/${this.intezmeny}`, {});
      this.jelez(r?.fut ? 'A generálás már fut.' : `Bizonylat: ${r?.uj || 0} új, ${r?.ervenytelen || 0} érvénytelenítve${r?.hiba ? `, ${r.hiba} hiba` : ''}.`);
      await this.frissit();
    } catch (e: any) { this.hiba = e?.error?.error || 'Nem sikerült.'; }
    this.bizFut = false;
  }

  async partnerTorol(p: any) {
    try {
      await firstValueFrom(this.http.delete(`/api/tandij/partner/${p.id}/${this.intezmeny}`));
      this.jelez('Az összerendelés törölve.');
      await this.frissit();
    } catch (e: any) { this.hiba = e?.error?.error || 'Nem sikerült.'; }
  }

  /* ── Összerendelés ── */
  get szurtPartnerek(): any[] {
    const k = this.partnerKereses.trim().toLowerCase();
    return (this.partnerek?.partnerek || []).filter((p: any) =>
      (!this.csakRendeletlen || !p.csalad)
      && (!k || [p.partner_nev, p.partner_szamla, p.csalad_nev, ...(p.kozlemenyek || [])].join(' ').toLowerCase().includes(k)));
  }
  get javaslatDb(): number { return this.javasoltak.length; }
  private get javasoltak(): any[] {
    return (this.partnerek?.partnerek || []).filter((p: any) => !p.csalad && !p.kategoria && (p.javaslat || p.javaslat_kategoria));
  }
  get kategoriaLista(): any[] {
    return Object.entries(this.partnerek?.kategoriak || {}).map(([kod, nev]) => ({ kod, nev }));
  }
  get egyebCsoportok(): any[] {
    return this.egyeb ? [...this.egyeb.kategoriak, this.egyeb.besorolatlan, this.egyeb.kizart] : [];
  }
  honapLista(k: any) {
    return Object.keys(k?.honapok || {}).sort().map((h) => ({ honap: h.replace('-', '. '), osszeg: k.honapok[h] }));
  }

  /** `cel`: család-azonosító, vagy 'kat:<kategória>' (normatíva, adomány, egyéb). */
  async hozzarendel(p: any, cel?: string) {
    const v = cel || this.valasztas[p.kulcs];
    if (!v) { this.hiba = 'Válaszd ki a családot vagy a kategóriát.'; return; }
    if (v === 'uj-leendo') { await this.leendoPartnerbol(p); return; }
    const kategoria = v.startsWith('kat:') ? v.slice(4) : null;
    try {
      await this.post(`/api/tandij/partner/${this.intezmeny}`, {
        csalad: kategoria ? null : v, kategoria, partner_szamla: p.partner_szamla, partner_nev: p.partner_nev });
      if (kategoria) { p.kategoria = kategoria; p.kategoria_nev = this.partnerek?.kategoriak?.[kategoria] || kategoria; }
      else { p.csalad = v; p.csalad_nev = (this.partnerek?.csaladok || []).find((c: any) => c.csalad === v)?.nev || ''; }
      this.jelez(`${p.partner_nev} → ${p.kategoria_nev || p.csalad_nev}`);
    } catch (e: any) { this.hiba = e?.error?.error || 'Nem sikerült az összerendelés.'; }
  }
  async javaslatokElfogadasa() {
    const lista = this.javasoltak;
    if (!lista.length || !confirm(`${lista.length} bankpartner besorolása a javaslat szerint (szülőnév-egyezés, illetve normatíva/adomány)?`)) return;
    for (const p of lista) await this.hozzarendel(p, p.javaslat || 'kat:' + p.javaslat_kategoria);
    await this.frissit();
  }

  /* ── Kiírás ── */
  kiirasElokeszit() {
    this.zsebSzerkElokeszit();
    if (this.kiirasCel && !this.zsebek.some((z: any) => z.id === this.kiirasCel)) this.kiirasCel = '';
    this.kiirasSorok = (this.egyenlegek?.csaladok || []).map((c: any) => ({
      csalad: c.csalad, nev: c.nev, osztalyok: this.osztalyok(c),
      gyerek_db: (c.gyerekek || []).filter((g: any) => g.aktiv).length,
      regi: c.kiiras ? c.kiiras.osszeg : null,
      osszeg: c.kiiras ? c.kiiras.osszeg : null,
      megjegyzes: c.kiiras?.megjegyzes || '',
      //  További zsebek: zsebenként egy oszlop.
      zs: Object.fromEntries(this.zsebek.map((z: any) => {
        const k = c.kiirasok?.[z.id];
        return [z.id, { regi: k ? k.osszeg : null, osszeg: k ? k.osszeg : null, megjegyzes: k?.megjegyzes || null }];
      })),
    }));
  }
  alapAlkalmaz() {
    const a = Number(this.kiirasAlap);
    if (!(a > 0)) return;
    for (const s of this.kiirasSorok) {
      const cel = this.kiirasCel ? s.zs[this.kiirasCel] : s;
      if (!cel) continue;
      if (this.kiirasMod === 'gyermek') cel.osszeg = s.gyerek_db ? a * s.gyerek_db : cel.osszeg;
      else cel.osszeg = a;
    }
  }
  kiirasOszlopOsszeg(zseb: string): number {
    return this.kiirasSorok.reduce((s, x) => s + (Number(zseb ? x.zs[zseb]?.osszeg : x.osszeg) || 0), 0);
  }
  get kiirasOsszeg(): number {
    return this.kiirasOszlopOsszeg('') + this.zsebek.reduce((s: number, z: any) => s + this.kiirasOszlopOsszeg(z.id), 0);
  }

  async kiirasMentes() {
    const valtozott = (x: any) => (Number(x.osszeg) || 0) !== (Number(x.regi) || 0) || x.regi !== null;
    const tetelek = [
      ...this.kiirasSorok.filter(valtozott)
        .map((s) => ({ csalad: s.csalad, zseb: '', osszeg: Number(s.osszeg) || 0, megjegyzes: s.megjegyzes || null })),
      ...this.kiirasSorok.flatMap((s) => Object.entries(s.zs as Record<string, any>).filter(([, x]) => valtozott(x))
        .map(([zseb, x]) => ({ csalad: s.csalad, zseb, osszeg: Number(x.osszeg) || 0, megjegyzes: x.megjegyzes || null }))),
    ];
    if (!tetelek.length) return;
    this.kiirasMent = true;
    try {
      const r: any = await this.post(`/api/tandij/kiiras/${this.intezmeny}`, { tanev: this.tanev, tetelek });
      this.jelez(`${r.mentve} kiírás mentve${r.torolve ? `, ${r.torolve} törölve` : ''}.`);
      await this.frissit();
    } catch (e: any) { this.hiba = e?.error?.error || 'Nem sikerült a kiírás.'; }
    finally { this.kiirasMent = false; }
  }
}
